import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, CheckCircle2, ArrowRight, Shield, Clock, Building2,
  TrendingUp, AlertTriangle, Star, Award, Wrench, DollarSign, Calendar
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { SEO } from '../components/shared/SEO';
import { COMPANY_INFO } from '../data/mock';

const COMMERCIAL_REVIEWS = [
  {
    name: 'James P.',
    title: 'Property Manager',
    property: 'Stonegate Apartments — Plano',
    text: 'We manage 12 apartment buildings and AAA handles all of our irrigation. Quarterly inspections have eliminated the surprise repair calls we used to get constantly. Our water bills dropped nearly 25% in the first year. These guys know commercial systems.',
  },
  {
    name: 'Karen L.',
    title: 'Facilities Director',
    property: 'Legacy Office Park — Frisco',
    text: 'Professional, licensed, and on time every single visit. AAA has been maintaining our 6-building office campus for 3 years. They document everything, send a detailed report after each visit, and their pricing is always exactly what was quoted.',
  },
  {
    name: 'Mike T.',
    title: 'HOA Board President',
    property: 'Creekside HOA — Allen',
    text: 'Our common area irrigation was a mess — dead patches, water running into the street, complaints from homeowners. AAA came in, did a full audit, and fixed everything. The quarterly program keeps it perfect. Homeowners are happy.',
  },
  {
    name: 'Sandra G.',
    title: 'Regional Manager',
    property: 'Cornerstone Retail Center — McKinney',
    text: 'We have 3 retail centers and needed a contractor we could trust to handle all of them consistently. AAA delivers every time. Tenants notice the difference in curb appeal. Worth every penny of the maintenance contract.',
  },
  {
    name: 'Derek W.',
    title: 'Operations Manager',
    property: 'Northgate Business Complex — Richardson',
    text: 'Called AAA after our previous contractor missed two scheduled visits and left a valve open all weekend — cost us thousands. AAA has been on time every visit for two years straight. Night and day difference in professionalism.',
  },
];

const PROPERTY_TYPES = [
  { icon: Building2, label: 'Apartment Complexes', desc: 'Multi-building residential communities with high-traffic landscaping demands' },
  { icon: Building2, label: 'Office Buildings & Parks', desc: 'Professional campuses where curb appeal directly reflects business credibility' },
  { icon: Building2, label: 'HOAs & Subdivisions', desc: 'Common area irrigation for entrances, medians, and shared green spaces' },
  { icon: Building2, label: 'Retail Centers & Storefronts', desc: 'Strip malls and standalone stores where landscaping drives foot traffic' },
  { icon: Building2, label: 'Industrial Facilities', desc: 'Large-lot properties requiring efficient, low-maintenance irrigation programs' },
  { icon: Building2, label: 'Churches & Schools', desc: 'Community properties that need reliable, budget-conscious irrigation management' },
];

export default function CommercialPage() {
  return (
    <Layout>
      <SEO
        title="Commercial &amp; HOA Irrigation Allen TX | AAA Irrigation"
        description="TCEQ licensed commercial irrigation for apartments, HOAs, office parks &amp; retail in Allen, Plano &amp; DFW. Quarterly inspections &amp; emergency repair. Call 469-751-3567."
        keywords="commercial irrigation Allen TX, HOA irrigation systems DFW, commercial sprinkler repair Plano, HOA irrigation maintenance McKinney, quarterly irrigation inspection Frisco, office park irrigation North Texas"
      />

      {/* ── HERO ── */}
      <section className="relative bg-[#0f172a] py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/commercial-repair.webp" alt="North Texas commercial irrigation trenching and installation job"
            className="w-full h-full object-cover opacity-20"
            fetchpriority="high"
            width="1920"
            height="1080" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#059669]/20 border border-[#059669]/40 rounded-full px-4 py-1.5 mb-6">
              <Shield className="w-4 h-4 text-[#059669]" />
              <span className="text-sm font-bold text-[#059669]">TCEQ Licensed Commercial Contractor — #{COMPANY_INFO.license}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black italic uppercase text-white leading-none mb-6">
              Commercial<br />
              <span className="text-[#1b7abf]">Irrigation</span><br />
              Services
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              We serve apartments, office parks, HOAs, retail centers, and industrial facilities
              across Plano, Allen, Frisco, and all of Collin County. Residential quality.
              Commercial reliability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`tel:${COMPANY_INFO.phone}`}
                className="inline-flex items-center justify-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-black text-xl px-8 py-5 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 uppercase tracking-wide">
                <Phone className="w-6 h-6" />
                {COMPANY_INFO.phoneFormatted}
              </a>
              <Link to={COMPANY_INFO.bookingUrl}
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border-2 border-white/40 text-white font-bold text-lg px-8 py-5 rounded-2xl transition-all duration-300">
                <Calendar className="w-5 h-5" />
                Schedule a Site Visit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHO WE SERVE ── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black italic uppercase text-slate-900 mb-3">
              We Serve <span className="text-[#1b7abf]">All Commercial Properties</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              From a single-building office to a 200-unit apartment complex — we have the equipment,
              experience, and licensing to handle it.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROPERTY_TYPES.map((pt, i) => (
              <div key={i} className="flex items-start gap-4 bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-[#1b7abf]/40 hover:shadow-md transition-all duration-200">
                <div className="w-12 h-12 bg-[#1b7abf]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-[#1b7abf]" />
                </div>
                <div>
                  <h3 className="font-black text-slate-900 uppercase tracking-wide text-base mb-1">{pt.label}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{pt.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY INSPECTIONS MATTER ── */}
      <section className="py-16 md:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 rounded-full px-4 py-1.5 mb-6">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm font-bold text-red-400 uppercase tracking-wide">Why It Matters</span>
              </div>
              <h2 className="text-4xl font-black italic uppercase text-white mb-6 leading-tight">
                Why Commercial Properties<br />
                <span className="text-[#1b7abf]">Need Regular Inspections</span>
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                A commercial irrigation system runs daily, often unmonitored. Unlike residential systems,
                a single faulty valve on a commercial property can waste hundreds of thousands of gallons
                and go undetected for months — until the water bill arrives.
              </p>
              <ul className="space-y-5">
                {[
                  {
                    icon: DollarSign,
                    title: 'Water Waste Is Expensive',
                    desc: 'A single stuck valve on a commercial property can add $800–$2,000 per month to your water bill. Regular inspections catch these issues before they compound.',
                  },
                  {
                    icon: AlertTriangle,
                    title: 'Liability & Property Damage',
                    desc: 'Overwatered areas near foundations, parking lots, and walkways create slip hazards and structural damage. Proper maintenance protects you legally and financially.',
                  },
                  {
                    icon: TrendingUp,
                    title: 'Curb Appeal = Property Value',
                    desc: 'Healthy, well-maintained landscaping increases perceived property value and tenant satisfaction. Brown patches and dead grass signal neglect to prospective tenants and buyers.',
                  },
                  {
                    icon: Shield,
                    title: 'Municipal Compliance',
                    desc: 'Many North Texas cities have water conservation ordinances. A properly calibrated system keeps you compliant and avoids fines.',
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#ea580c]/20 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-5 h-5 text-[#ea580c]" />
                    </div>
                    <div>
                      <p className="font-black text-white uppercase tracking-wide text-sm mb-1">{item.title}</p>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img src="/pressure-testing.webp" alt="Commercial irrigation inspection and pressure testing"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
                loading="lazy"
                width="800"
                height="500" />
            </div>
          </div>
        </div>
      </section>

      {/* ── ROI OF QUARTERLY INSPECTIONS ── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-1.5 mb-4">
              <TrendingUp className="w-4 h-4 text-[#059669]" />
              <span className="text-sm font-bold text-[#059669] uppercase tracking-wide">Return on Investment</span>
            </div>
            <h2 className="text-4xl font-black italic uppercase text-slate-900 mb-4">
              The ROI of <span className="text-[#1b7abf]">Quarterly Inspections</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Commercial clients who invest in quarterly inspections consistently see lower total costs
              than those who call us only when something breaks.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                stat: '20–40%',
                label: 'Average Water Bill Reduction',
                desc: 'Properly calibrated and maintained systems eliminate waste from misaligned heads, stuck valves, and pressure issues — often cutting water costs by 20–40%.',
                color: '#059669',
              },
              {
                stat: '5–10×',
                label: 'Cost Savings vs. Emergency Repairs',
                desc: 'Catching a $150 valve issue during a scheduled inspection prevents the $800–$2,000 emergency repair — and the water damage — that comes from letting it fail.',
                color: '#1b7abf',
              },
              {
                stat: '100%',
                label: 'Documented Every Visit',
                desc: 'We document every zone, every head, and every valve condition after each inspection. You have a complete system record for insurance, audits, and handoffs.',
                color: '#ea580c',
              },
              {
                stat: 'Zero',
                label: 'Surprise Breakdowns',
                desc: 'Seasonal adjustments, worn parts, and pressure changes are addressed proactively — before peak summer heat turns a small issue into a flooded common area.',
                color: '#059669',
              },
              {
                stat: '+15%',
                label: 'Tenant Retention Impact',
                desc: 'Studies show well-maintained exterior landscaping improves tenant satisfaction scores by 15% or more — directly impacting lease renewals and vacancy rates.',
                color: '#1b7abf',
              },
              {
                stat: 'Priority',
                label: 'Emergency Response for Contract Clients',
                desc: 'Quarterly maintenance clients receive priority scheduling for emergency repairs. When something does go wrong, you\'re first in line — not waiting days for a callback.',
                color: '#ea580c',
              },
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-7 border border-slate-200 hover:shadow-lg transition-all duration-200">
                <div className="text-4xl font-black mb-2" style={{ color: item.color }}>{item.stat}</div>
                <h3 className="font-black text-slate-900 uppercase tracking-wide text-sm mb-3">{item.label}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* What's included */}
          <div className="bg-[#1b7abf] rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-black italic uppercase text-white mb-4">
                  What's Included in<br />a Quarterly Inspection
                </h3>
                <p className="text-blue-100 leading-relaxed mb-6">
                  Every quarterly visit is a full system audit — not just a quick walkthrough.
                  We check everything, document everything, and fix what we find on the spot.
                </p>
                <a href={`tel:${COMPANY_INFO.phone}`}
                  className="inline-flex items-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-black px-8 py-4 rounded-xl transition-all duration-300 uppercase tracking-wide">
                  <Phone className="w-5 h-5" />
                  Call to Schedule
                </a>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'Full zone-by-zone system run',
                  'Head alignment & coverage check',
                  'Valve operation & leak test',
                  'Controller programming review',
                  'Pressure testing & adjustment',
                  'Seasonal schedule optimization',
                  'Worn parts identified & replaced',
                  'Written inspection report',
                  'Photo documentation',
                  'Next-visit recommendations',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#059669] flex-shrink-0" />
                    <span className="text-white text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMMERCIAL REVIEWS ── */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-[#1b7abf] text-white px-6 py-3 rounded-full shadow-lg mb-6">
              <Award className="w-5 h-5 text-yellow-300" />
              <span className="font-black uppercase tracking-widest text-sm">Top Rated Commercial Irrigation — North Texas</span>
              <Award className="w-5 h-5 text-yellow-300" />
            </div>
            <div className="flex items-center justify-center gap-1 mb-3">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />)}
            </div>
            <h2 className="text-4xl font-black italic uppercase text-slate-900 mb-2">
              What Commercial Clients Say
            </h2>
            <p className="text-slate-500 font-medium">Trusted by Property Managers, HOAs, and Facility Directors Across Collin County</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMMERCIAL_REVIEWS.map((r, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-slate-100 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex">
                    {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                  </div>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Google</span>
                </div>
                <p className="text-slate-700 text-sm leading-relaxed flex-1 mb-5">&ldquo;{r.text}&rdquo;</p>
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#1b7abf] rounded-full flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                      {r.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-sm">{r.name} — {r.title}</p>
                      <p className="text-slate-400 text-xs">{r.property}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-16 bg-[#1b7abf]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Wrench className="w-12 h-12 text-white/60 mx-auto mb-4" />
          <h2 className="text-4xl font-black italic uppercase text-white mb-4">
            Ready to Protect Your Property?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Call us to schedule a commercial site inspection. We'll assess your system, give you
            a straightforward quote, and get you on a maintenance schedule that works for your budget.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={`tel:${COMPANY_INFO.phone}`}
              className="inline-flex items-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-black text-xl px-10 py-5 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 uppercase tracking-wide w-full sm:w-auto justify-center">
              <Phone className="w-6 h-6" />
              {COMPANY_INFO.phoneFormatted}
            </a>
            <Link to={COMPANY_INFO.bookingUrl}
              className="inline-flex items-center gap-2 bg-white text-[#1b7abf] hover:bg-blue-50 font-black text-lg px-10 py-5 rounded-2xl transition-all duration-300 uppercase tracking-wide w-full sm:w-auto justify-center">
              <Calendar className="w-5 h-5" />
              Request a Site Visit
            </Link>
          </div>
        </div>
      </section>

    </Layout>
  );
}
